// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import "modernizr";
import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";

import { MainPanel } from "types";
import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionCreateReport } from "actions";
import { Report } from "dbtypes";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";

import { IReportState } from "states/guistates";
import { isSet } from "utils";

interface IReportEntryExtProps {
  id: number;
  isReadOnly: boolean;
}

interface IReportEntryProps {
  report: Report | undefined;
  reportLink: IReportState | undefined;
  isReportTimedout: boolean;
}

const mapStateToProps = (state: IState, ownProps: IReportEntryExtProps) => {
  const siteId = state.cached.selectedSite;
  const site = state.site.siteList?.find((curSite) => curSite.id === siteId);
  if (site === undefined) {
    console.error("admin unidentified site", siteId, state.site.siteList);
  }

  const REQ_TIMEOUT = 15 * 60 * 1000; // 15 min
  const reportLink = state.gui.reportLinks[ownProps.id] ?? undefined;
  const isReportTimedout =
    isSet(reportLink?.requested) && Date.now() - REQ_TIMEOUT >= reportLink!.requested;

  const props: IReportEntryProps = {
    report: site?.cloudSettings?.reports?.[ownProps.id],
    reportLink,
    isReportTimedout,
  };
  return props;
};

interface IReportsDispatch {
  onEditReport: (index: number) => void;
  onCreateReport: (index: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IReportsDispatch = {
    onEditReport: (index: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_REPORT, index));
    },
    onCreateReport: (index: number) => {
      const payload: IActionCreateReport = {
        reportId: index,
        previewOnly: false,
      };
      dispatch(raiseAction(ActionType.ACT_CREATE_REPORT_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class ReportsComponent extends React.Component<
  IReportEntryProps & IReportEntryExtProps & IReportsDispatch & WrappedComponentProps
> {
  public render() {
    const LINK_TIMEOUT = 24 * 3600 * 1000; // 1 day
    const isReportRequested =
      isSet(this.props.reportLink?.requested) &&
      Date.now() - LINK_TIMEOUT < this.props.reportLink!.requested;
    // extract timestamp from name (format: path/name_timestamp.suffix)
    const linkParts = (this.props.reportLink?.link ?? "").split(/[_.]/);
    const reportTimestamp = parseInt(linkParts[linkParts.length - 2] ?? "");
    const isReportValid = Date.now() - LINK_TIMEOUT < reportTimestamp;
    const linkClasses = [];
    if (!this.props.report?.isActive) linkClasses.push("deactivated");
    if (!this.props.isReadOnly) linkClasses.push("link");

    return (
      <tr>
        <td>
          <span
            onClick={() => {
              if (!this.props.isReadOnly) this.props.onEditReport(this.props.id);
            }}
            className={linkClasses.join(" ")}
          >
            {this.props.report?.title}
          </span>
        </td>
        <td>
          {this.props.report?.isActive && (
            <Button variant="secondary" className="mb-1">
              <Link to={`../${MainPanel.REPORTDATAVIEW}?report=${this.props.id}`}>
                <FormattedMessage id="View Report Online" />
              </Link>
            </Button>
          )}
        </td>
        <td>
          {this.props.report?.isActive && (
            <Button
              variant="secondary"
              className="mb-1"
              onClick={(e) => this.props.onCreateReport(this.props.id)}
            >
              {isReportValid ? (
                <FormattedMessage id="Update Report File" />
              ) : (
                <FormattedMessage id="Create Report File" />
              )}
            </Button>
          )}
        </td>
        <td>
          {this.props.report?.isActive && (
            <>
              {isReportValid && (
                <a
                  href={this.props.reportLink?.link}
                  target="_blank"
                  rel="noreferrer"
                  className="me-2"
                >
                  <FormattedMessage id="Download" /> (<FormattedDate valueMs={reportTimestamp} />{" "}
                  <FormattedTime valueMs={reportTimestamp} />)
                </a>
              )}
              {!isReportValid &&
                isReportRequested &&
                (isSet(this.props.reportLink?.error) ? (
                  <span className="error">{this.props.reportLink?.error}</span>
                ) : this.props.isReportTimedout ? (
                  <span className="error">
                    <FormattedMessage id="Report generation timed out. Try reducing report content." />
                  </span>
                ) : (
                  <div className="spinner">
                    <FormattedMessage id="generating report" />
                    <div id="loading-spinner" />
                  </div>
                ))}
            </>
          )}
        </td>
      </tr>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ReportsComponent));
